import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import FormatValue from 'assets/components/formatValue/FormatValue';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import TextBox, { textBoxStyles } from 'assets/components/textBox/TextBox';
import { getLocales } from 'assets/locales/Locale';
import ValueDisplay from 'assets/templates/valueDisplay/ValueDisplay';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { formatPercentage, roundToStep } from 'assets/utils/data/Number';
import { isEmpty } from 'lodash';
import PurchaseState from 'models/enums/PurchaseState';
import ContractRefundPriceSummariesTable from '../../../components/ContractRefundPriceSummariesTable';
import { getTermStartEffectiveDate } from '../../../components/RefundInfoTable';
import { ContractRefundForm, useContractRefundProvider } from '../../../Sales.ContractRefund.Hooks';
import { refundInfoGridStyles } from './Sales.ContractRefund.Process.Info';

const SalesContractRefundProcessContract = () => {
  const { lang, translate } = getLocales();
  const { form } = useModalForm<ContractRefundForm>();
  const { contract, vehicle } = useContractRefundProvider();

  const termStartEffectiveDate = getTermStartEffectiveDate(
    form.data?.refundConfigTemplateJson ?? form.data?.refundConfigTemplate,
    contract
  );
  return (
    <FormWrapper>
      <h3>{lang.sumOfClaimPreTaxAmountsPaidOnContract.toLocaleUpperCase()}</h3>
      <div style={{ ...refundInfoGridStyles, gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.total}
          subtitle={<FormatValue value={`$${form.data?.sumOfClaimAmountsPaidOnContract ?? 0}`} />}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.gstHst}
          subtitle={<FormatValue value={`$${form.data?.sumOfClaimGstHstAmountsPaidOnContract ?? 0}`} />}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.pst}
          subtitle={<FormatValue value={`$${form.data?.sumOfClaimProvincialSalesTaxAmountsPaidOnContract ?? 0}`} />}
        />
      </div>
      <br />
      <hr />
      <br />
      {(!isEmpty(form.data?.enhancements) || !isEmpty(form.data?.subfees)) && (
        <>
          <div style={{ ...refundInfoGridStyles, gridTemplateColumns: 'repeat(2, 1fr)' }}>
            {!isEmpty(form.data?.enhancements) && (
              <TextBox
                class={textBoxStyles.block.flexBlock}
                title={lang.enhancements}
                subtitle={
                  <ul>
                    {form.data?.enhancements?.map((it) => {
                      return <li key={it.enhancementId}>{it.name}</li>;
                    })}
                  </ul>
                }
              />
            )}
            {!isEmpty(form.data?.subfees) && (
              <TextBox
                class={textBoxStyles.block.flexBlock}
                title={lang.surcharges}
                subtitle={
                  <ul>
                    {form.data?.subfees?.map((it) => {
                      return <li key={it.subfeeId}>{it.name}</li>;
                    })}
                  </ul>
                }
              />
            )}
          </div>
          <br />
          <hr />
          <br />
        </>
      )}
      <h3>{lang.vehicle}</h3>
      <div style={refundInfoGridStyles}>
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.purchaseState}
          subtitle={translate(
            vehicle?.purchaseState === PurchaseState.USED ? PurchaseState.PRE_OWNED : vehicle?.purchaseState
          )}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.purchaseType}
          subtitle={translate(vehicle?.purchaseType) ?? 'N/A'}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.startingOdometer}
          subtitle={<ValueDisplay value={`${vehicle?.posOdometerReading} km`} />}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.maxCoverageKm}
          subtitle={<ValueDisplay value={`${contract?.maxCoverageKm ?? 0} km`} />}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.kmsUsed}
          subtitle={<ValueDisplay value={`${form.data.usedOdometerReading} km`} />}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.kmConsumed}
          subtitle={formatPercentage(form.data.usedOdometerReadingPercentage)}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.kmRemaining}
          subtitle={formatPercentage(form.data.remainingOdometerReadingPercentage)}
        />
      </div>
      <br />
      <hr />
      <br />
      <h3>{lang.termAndExpiryInformation}</h3>
      <div style={refundInfoGridStyles}>
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.effectiveDate}
          subtitle={localizeDate(termStartEffectiveDate, defaultDateTimeFormat)}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.expiryDate}
          subtitle={localizeDate(contract?.expiryDate, defaultDateTimeFormat)}
        />
        <TextBox class={textBoxStyles.block.flexBlock} title={lang.termInDays} subtitle={form.data?.termInDays} />
        <TextBox class={textBoxStyles.block.flexBlock} title={lang.daysConsumed} subtitle={form.data?.usedTermInDays} />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.daysRemaining}
          subtitle={form.data?.remainingTermInDays}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.termConsumed}
          subtitle={formatPercentage(form.data?.usedTermInDaysPercentage)}
        />
        <TextBox
          class={textBoxStyles.block.flexBlock}
          title={lang.termRemaining}
          subtitle={formatPercentage(form.data?.remainingTermInDaysPercentage)}
        />
      </div>
      <br />
      <hr />
      <br />
      <h3 style={{ paddingBottom: '0' }}>{lang.contractPricing}</h3>
      <div style={{ ...refundInfoGridStyles, gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>
            <strong>{lang.dealerCost}</strong>
          </span>
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.paymentPrice}
            subtitle={<FormatValue value={`$${roundToStep(contract?.paymentPrice, 0.01)}`} />}
          />
          <br />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.enhacementsPriceCAD}
            subtitle={<FormatValue value={`$${contract?.enhancementsPriceCAD}`} />}
          />
          <br />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.surchargesPriceCAD}
            subtitle={<FormatValue value={`$${contract?.surchargesPriceCAD}`} />}
          />
          <br />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.paymentTaxes}
            subtitle={<FormatValue value={`$${roundToStep(contract?.dealerCostTaxAmt, 0.01)}`} />}
          />
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.totalPriceAmountWPaymentTaxes}
            subtitle={<FormatValue value={`$${roundToStep(contract?.dealerCostTotal, 0.01)}`} />}
          />
        </div>
        <div>
          <span>
            <strong>{lang.salePrice}</strong>
          </span>
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.salePrice}
            subtitle={<FormatValue value={`$${roundToStep(contract?.salePriceCAD, 0.01)}`} />}
          />
          <br />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.salePriceTaxes}
            subtitle={<FormatValue value={`$${roundToStep(contract?.totalTaxCAD, 0.01)}`} />}
          />
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.totalRetailPriceAmountWSalePriceTaxes}
            subtitle={<FormatValue value={`$${roundToStep(contract?.salePriceWithTaxCAD, 0.01)}`} />}
          />
        </div>
        <div>
          <span>
            <strong>{lang.dealerProfitNonASC}</strong>
          </span>
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.dealerProfit}
            subtitle={<FormatValue value={`$${roundToStep(contract?.dealerProfit, 0.01)}`} />}
          />
          <br />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.dealerProfitTax}
            subtitle={<FormatValue value={`$${roundToStep(contract?.dealerProfitTaxAmt, 0.01)}`} />}
          />
          <hr />
          <TextBox
            class={textBoxStyles.block.flexBlock}
            title={lang.totalDealerProfitWDealerProfitTaxes}
            subtitle={<FormatValue value={`$${roundToStep(contract?.dealerProfitWithTaxCAD, 0.01)}`} />}
          />
        </div>
      </div>
      &nbsp;{' '}
      <ContractRefundPriceSummariesTable contract={contract} contractRefund={form.data as Model.IContractRefund} />
    </FormWrapper>
  );
};

export default SalesContractRefundProcessContract;
